import { ChatEventKind, Message, RequestType, ResponseStatus, SenderRole } from "@handshake/core-types";

export type ReleaseRequestVariant = "full" | "drawdown" | "sub_milestone";

export const FUND_RELEASE_COPY = {
  project: {
    fundReleaseRequested: "Fund release requested",
    reviewFundReleaseRequest: "Review fund release request",
  },
  requests: {
    actionRequired: "Action required",
    totalMilestoneValue: "Total milestone value",
    releasedFromEscrow: "Released from escrow",
    currentlyInEscrow: "Currently in escrow",
    amountRequestedForRelease: "Amount requested for release",
    includedInThisRequest: "Included in this request",
    includedInThisRequestResolved: "Sub-milestones included in this request",
    reject: "Reject",
    review: "Review",
    viewSupportingDocument: "View supporting document",
    viewSupportingDocuments: "View supporting documents",
    releasedFromEscrowOutcome: "Released from escrow",
    fundsRemainedInEscrow: "Funds remained in escrow (no change)",
    rejectionReason: "Reason for rejection",
    noRequestsYet: "No requests yet",
    noRequestsFallback: "Fund release requests will appear here for your approval.",
    protectedMoney: "The money stayed protected and never moved.",
    typeBadge: {
      full: "Full release",
      drawdown: "Drawdown",
      subMilestone: "Sub-milestone release",
    },
  },
  modal: {
    approveFundRelease: "Approve Fund Release?",
    amountToRelease: "Amount to release",
    associatedSubMilestones: "Associated sub-milestones",
    pendingSubRequestsAlsoApproved:
      "Approving this milestone release will also auto-approve the pending sub-milestone request(s) below and release the full remaining escrow.",
    goBack: "Go back",
    approve: "Approve Fund Release",
    warning: "This can't be reversed. Only approve if you're happy with the work delivered.",
  },
  chat: {
    approvedRelease: "You approved this release.",
  },
} as const;

export function formatFirmPossessive(firmName?: string | null): string {
  const trimmed = firmName?.trim();
  if (!trimmed) {
    return "Business's";
  }
  return trimmed.endsWith("s") ? `${trimmed}'` : `${trimmed}'s`;
}

export type IncludedSubMilestoneItem = {
  id: string;
  name: string;
  amount: number;
};

export type MilestoneSubLookup = {
  id: string;
  subMilestones?: Array<{
    id: string;
    name: string;
    budgetAmount?: number;
    paymentRequestStatus?: string;
  }> | null;
};

export function getReleaseRequestVariant(message: Message): ReleaseRequestVariant {
  if (message.request?.requestType === RequestType.DRAWDOWN) {
    return "drawdown";
  }
  if (message.request?.metadata?.isSubmilestone) {
    if (isFullMilestoneSubBatchRelease(message)) {
      return "full";
    }
    return "sub_milestone";
  }
  return "full";
}

/** Sub batch that covers the full milestone budget/escrow is treated as an entire milestone release. */
export function isFullMilestoneSubBatchRelease(message: Message): boolean {
  const metadata = message.request?.metadata;
  if (!metadata?.isSubmilestone) {
    return false;
  }
  const batchItems = getIncludedSubMilestonesFromRequest(message);
  if (!batchItems.length) {
    return false;
  }
  const batchTotal = batchItems.reduce((sum, item) => sum + item.amount, 0);
  const milestoneBudget = Number(metadata.milestone?.budgetAmount ?? 0);
  return milestoneBudget > 0 && batchTotal >= milestoneBudget;
}

/** Milestone-level request for less than the full milestone budget (e.g. remainder after subs). */
export function isPartialMilestoneRemainderRelease(message: Message): boolean {
  if (message.request?.metadata?.isSubmilestone) {
    return false;
  }
  if (message.request?.requestType !== RequestType.MILESTONE_COMPLETED) {
    return false;
  }
  const amount = getReleaseRequestDisplayAmount(message);
  const budget = Number(message.request?.metadata?.milestone?.budgetAmount ?? 0);
  return budget > 0 && amount != null && amount < budget;
}

/** True when a sub-milestone batch request has a sibling milestone-level remainder request. */
export function hasCompanionMilestoneRemainderRequest(message: Message, messages: Message[] = []): boolean {
  if (!message.request?.metadata?.isSubmilestone) {
    return false;
  }
  const milestoneId = message.request.metadata.milestoneId;
  if (!milestoneId) {
    return false;
  }
  return messages.some(
    (item) =>
      item.request?.requestType === RequestType.MILESTONE_COMPLETED &&
      !item.request?.metadata?.isSubmilestone &&
      item.request?.metadata?.milestoneId === milestoneId &&
      !item.responseId &&
      !item.request?.messageResponse
  );
}

/** True when a milestone-level request has a sibling sub-milestone batch request. */
export function hasCompanionSubMilestoneReleaseRequest(message: Message, messages: Message[] = []): boolean {
  if (message.request?.metadata?.isSubmilestone) {
    return false;
  }
  const milestoneId = message.request?.metadata?.milestoneId;
  if (!milestoneId) {
    return false;
  }
  return messages.some(
    (item) =>
      item.request?.requestType === RequestType.MILESTONE_COMPLETED &&
      item.request?.metadata?.isSubmilestone &&
      item.request?.metadata?.milestoneId === milestoneId &&
      !item.responseId &&
      !item.request?.messageResponse
  );
}

export function getReleaseRequestSubjectLine(message: Message, messages: Message[] = [], pastTense = false): string {
  const variant = getReleaseRequestVariant(message);
  const hasCompanion =
    hasCompanionMilestoneRemainderRequest(message, messages) || hasCompanionSubMilestoneReleaseRequest(message, messages);
  const companionPendingOnMain = getCompanionPendingSubReleaseTotal(message, messages) > 0;
  const subject =
    variant === "drawdown"
      ? "a drawdown"
      : hasCompanion && !companionPendingOnMain
        ? "milestone and sub-milestone releases"
        : companionPendingOnMain
          ? "a full milestone release"
          : variant === "sub_milestone"
            ? getIncludedSubMilestonesFromRequest(message).length > 1
              ? "a sub-milestones release"
              : "a sub-milestone release"
            : isPartialMilestoneRemainderRelease(message)
              ? "a remaining milestone release"
              : "a full milestone release";
  return pastTense ? `requested ${subject}.` : `has requested ${subject}.`;
}

export function getReleaseRequestCardTitle(milestoneName: string): string {
  const trimmed = milestoneName?.trim();
  return trimmed ? `Milestone: ${trimmed}` : "Milestone";
}

/** Short type badge shown on Requests-tab cards (HAN-197 design). */
export function getReleaseRequestTypeBadgeLabel(message: Message): string | null {
  const requestType = message.request?.requestType;
  if (requestType === RequestType.DELAY || requestType === RequestType.DISPUTE) {
    return null;
  }

  const variant = getReleaseRequestVariant(message);
  if (variant === "drawdown") {
    return FUND_RELEASE_COPY.requests.typeBadge.drawdown;
  }
  if (variant === "sub_milestone") {
    return FUND_RELEASE_COPY.requests.typeBadge.subMilestone;
  }
  return FUND_RELEASE_COPY.requests.typeBadge.full;
}

/** Request timestamp format from HAN-197 design: "29 Jun 2026, 11:15 AM". */
export function formatReleaseRequestTimestamp(value?: string | Date | null): string {
  if (!value) {
    return "";
  }
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "";
  }
  const day = date.getDate();
  const month = date.toLocaleString("en-GB", { month: "short" });
  const year = date.getFullYear();
  const time = date
    .toLocaleString("en-US", { hour: "numeric", minute: "2-digit", hour12: true })
    .replace(/\s+/g, " ");
  return `${day} ${month} ${year}, ${time}`;
}

export function getAttachmentDisplayName(url: string): string {
  const safeDecode = (value: string): string => {
    try {
      return decodeURIComponent(value);
    } catch {
      return value;
    }
  };

  try {
    const pathname = new URL(url, "https://example.com").pathname;
    const raw = safeDecode(pathname.split("/").pop() || "");
    return raw || "Supporting document";
  } catch {
    const raw = safeDecode(String(url).split("?")[0].split("/").pop() || "");
    return raw || "Supporting document";
  }
}

export function isPdfAttachment(url: string): boolean {
  const value = String(url || "").toLowerCase();
  return value.includes(".pdf") || value.includes("application/pdf");
}

/** Pending companion sub-milestone release messages for the same parent milestone. */
export function getCompanionPendingSubReleaseMessages(message: Message, messages: Message[] = []): Message[] {
  if (message.request?.metadata?.isSubmilestone) {
    return [];
  }
  const milestoneId = message.request?.metadata?.milestoneId;
  if (!milestoneId) {
    return [];
  }
  return messages.filter(
    (item) =>
      item.request?.requestType === RequestType.MILESTONE_COMPLETED &&
      item.request?.metadata?.isSubmilestone === true &&
      item.request?.metadata?.milestoneId === milestoneId &&
      !item.responseId &&
      !item.request?.messageResponse
  );
}

/** Sum of pending sub-milestone release requests on the same parent milestone. */
export function getCompanionPendingSubReleaseTotal(message: Message, messages: Message[] = []): number {
  return getCompanionPendingSubReleaseMessages(message, messages).reduce((sum, item) => {
    const subItems = getIncludedSubMilestonesFromRequest(item);
    if (subItems.length > 0) {
      return sum + subItems.reduce((subSum, sub) => subSum + sub.amount, 0);
    }
    return sum + (Number(item.request?.metadata?.milestoneOrSubMilestoneValue ?? 0) || 0);
  }, 0);
}

/** Sub rows that will be auto-approved if this milestone request is approved now. */
export function getCompanionPendingSubMilestonesForDisplay(
  message: Message,
  messages: Message[] = [],
  milestones: MilestoneSubLookup[] = []
): IncludedSubMilestoneItem[] {
  const rows: IncludedSubMilestoneItem[] = [];
  const seen = new Set<string>();
  for (const companion of getCompanionPendingSubReleaseMessages(message, messages)) {
    for (const sub of getIncludedSubMilestonesForRequestDisplay(companion, milestones)) {
      if (seen.has(sub.id)) {
        continue;
      }
      seen.add(sub.id);
      rows.push(sub);
    }
  }
  return rows;
}

/**
 * Hero amount for fund-release request cards and modals.
 * Milestone-level pending requests always release live remaining escrow on approve —
 * so when currentEscrow is known, prefer that over stale request metadata.
 * While a companion sub is still pending (and escrow is unknown), show remainder + sub total.
 *
 * Resolved (accepted/rejected) cards must use historical request metadata — live escrow is
 * already reduced after approval and would incorrectly show SGD 0 (HAN-197 audit bug).
 */
export function getReleaseRequestDisplayAmount(
  message: Message,
  messages: Message[] = [],
  currentEscrow?: number | null,
  options?: { status?: "pending" | "accepted" | "rejected" }
): number | null {
  const request = message.request;
  if (!request) {
    return null;
  }

  if (request.requestType === RequestType.DRAWDOWN) {
    const amount = request.metadata?.amount;
    return amount != null ? Number(amount) : null;
  }

  if (request.requestType !== RequestType.MILESTONE_COMPLETED) {
    const value = request.metadata?.milestoneOrSubMilestoneValue;
    return value != null ? Number(value) : null;
  }

  if (request.metadata?.isSubmilestone) {
    const batchItems = getIncludedSubMilestonesFromRequest(message);
    if (batchItems.length > 0) {
      return batchItems.reduce((sum, item) => sum + item.amount, 0);
    }
    const subValue = request.metadata?.milestoneOrSubMilestoneValue;
    if (subValue != null) {
      return Number(subValue);
    }
    const subBudget = request.metadata?.subMilestone?.budgetAmount;
    return subBudget != null ? Number(subBudget) : null;
  }

  const isResolved = options?.status === "accepted" || options?.status === "rejected";

  // Pending milestone approval always transfers remaining escrow — prefer live value.
  // Never do this for resolved audit cards: live escrow is already post-release.
  if (!isResolved && currentEscrow != null && currentEscrow >= 0) {
    return Math.round(currentEscrow * 100) / 100;
  }

  const amountInEscrow = request.metadata?.amountInEscrow;
  const milestoneValue = request.metadata?.milestoneOrSubMilestoneValue;
  const base = amountInEscrow != null ? Number(amountInEscrow) : milestoneValue != null ? Number(milestoneValue) : null;
  if (base == null) {
    return null;
  }

  // Resolved cards are a point-in-time record — do not inflate with later companion state.
  if (isResolved) {
    return base;
  }

  const companionSubTotal = getCompanionPendingSubReleaseTotal(message, messages);
  if (companionSubTotal <= 0) {
    return base;
  }

  const milestoneBudget = Number(request.metadata?.milestone?.budgetAmount ?? 0);
  // Metadata already stores full escrow (legacy) — keep it.
  if (milestoneBudget > 0 && base >= milestoneBudget - 0.01) {
    return base;
  }
  // Metadata stores remainder only — inflate to full release amount for display.
  return Math.max(0, Math.round((base + companionSubTotal) * 100) / 100);
}

/** Escrow balance before this release, from request metadata when available. */
export function getReleaseRequestHistoricalBefore(
  message: Message,
  releaseAmount: number
): number | null {
  const metadata = message.request?.metadata;
  if (!metadata) {
    return releaseAmount > 0 ? releaseAmount : null;
  }

  const storedEscrow = metadata.amountInEscrow != null ? Number(metadata.amountInEscrow) : null;

  if (message.request?.requestType === RequestType.DRAWDOWN) {
    if (storedEscrow != null && storedEscrow > 0) {
      return storedEscrow;
    }
    return releaseAmount > 0 ? releaseAmount : null;
  }

  if (metadata.isSubmilestone) {
    // Sub requests store parent remaining escrow at request time.
    if (storedEscrow != null && storedEscrow > 0) {
      return storedEscrow;
    }
    return releaseAmount > 0 ? releaseAmount : null;
  }

  // Milestone-level: amountInEscrow is remaining escrow targeted by this request.
  // If the approved release was larger (companion auto-approve), before was at least the release amount.
  if (storedEscrow != null && storedEscrow > 0) {
    return Math.max(storedEscrow, releaseAmount);
  }

  if (metadata.milestoneOrSubMilestoneValue != null) {
    const value = Number(metadata.milestoneOrSubMilestoneValue);
    if (value > 0) {
      return Math.max(value, releaseAmount);
    }
  }

  return releaseAmount > 0 ? releaseAmount : null;
}

/** Sub-milestone rows shown inside active/resolved fund-release cards (HAN-197 §5.1.3). */
export function getIncludedSubMilestonesFromRequest(message: Message): IncludedSubMilestoneItem[] {
  const metadata = message.request?.metadata;
  if (!metadata?.isSubmilestone) {
    return [];
  }

  const batchItems = metadata.subMilestones;
  if (Array.isArray(batchItems) && batchItems.length > 0) {
    return batchItems.map((item: { id?: string; name?: string; amount?: number; budgetAmount?: number }) => ({
      id: item.id || "",
      name: item.name || "Sub-milestone",
      amount: Number(item.amount ?? item.budgetAmount ?? 0),
    }));
  }

  if (metadata.subMilestone) {
    const amount =
      metadata.subMilestone.budgetAmount != null
        ? Number(metadata.subMilestone.budgetAmount)
        : Number(metadata.milestoneOrSubMilestoneValue ?? 0);
    return [
      {
        id: metadata.subMilestone.id,
        name: metadata.subMilestone.name || "Sub-milestone",
        amount,
      },
    ];
  }

  return [];
}

function isSubApproved(status?: string): boolean {
  return String(status ?? "").toLowerCase() === "approved";
}

function isSubPending(status?: string): boolean {
  return String(status ?? "").toLowerCase() === "pending";
}

function mapSubToIncludedItem(sub: { id: string; name: string; budgetAmount?: number }): IncludedSubMilestoneItem {
  return {
    id: sub.id,
    name: sub.name,
    amount: Number(sub.budgetAmount ?? 0),
  };
}

/**
 * Sub-milestone rows for Requests tab cards — from request metadata, with milestone fallback
 * for remainder releases that were sent as milestone-level requests.
 */
export function getIncludedSubMilestonesForRequestDisplay(
  message: Message,
  milestones: MilestoneSubLookup[] = []
): IncludedSubMilestoneItem[] {
  const fromRequest = getIncludedSubMilestonesFromRequest(message);
  if (fromRequest.length > 0) {
    return fromRequest;
  }

  const metadata = message.request?.metadata;
  if (metadata?.isSubmilestone) {
    return [];
  }

  const milestoneId = metadata?.milestoneId;
  if (!milestoneId) {
    return [];
  }

  // Prefer matching against stored remainder (without companion inflation).
  const storedAmount =
    metadata.amountInEscrow != null
      ? Number(metadata.amountInEscrow)
      : metadata.milestoneOrSubMilestoneValue != null
        ? Number(metadata.milestoneOrSubMilestoneValue)
        : null;

  const milestone = milestones.find((m) => m.id === milestoneId);
  const subs = milestone?.subMilestones ?? [];
  if (!subs.length) {
    return [];
  }

  const pendingSubs = subs.filter((sub) => isSubPending(sub.paymentRequestStatus));
  if (pendingSubs.length > 0 && storedAmount != null) {
    const pendingTotal = pendingSubs.reduce((sum, sub) => sum + Number(sub.budgetAmount ?? 0), 0);
    if (Math.abs(pendingTotal - storedAmount) < 0.01) {
      return pendingSubs.map(mapSubToIncludedItem);
    }
  }

  const unreleasedSubs = subs.filter((sub) => !isSubApproved(sub.paymentRequestStatus));
  const unreleasedTotal = unreleasedSubs.reduce((sum, sub) => sum + Number(sub.budgetAmount ?? 0), 0);
  if (unreleasedSubs.length > 0 && storedAmount != null && Math.abs(unreleasedTotal - storedAmount) < 0.01) {
    return unreleasedSubs.map(mapSubToIncludedItem);
  }

  return [];
}

export function shouldShowIncludedSubMilestonesInRequest(
  message: Message,
  milestones: MilestoneSubLookup[] = []
): boolean {
  return (
    getIncludedSubMilestonesForRequestDisplay(message, milestones).length > 0 &&
    !isFullMilestoneSubBatchRelease(message)
  );
}

/**
 * Before/after escrow always uses live remaining escrow for the milestone.
 * Do not pre-subtract companion pending requests — after the first approval,
 * remaining escrow updates and the next card's before/after updates with it.
 */
export function getRequestEscrowBeforeForDisplay(
  _message: Message,
  currentEscrow: number,
  _messages: Message[] = []
): number {
  return currentEscrow;
}

export function getReleaseRequestEscrowTransition(
  message: Message,
  currentEscrow: number,
  messages: Message[] = [],
  status: "pending" | "accepted" | "rejected" = "pending"
) {
  const amount = getReleaseRequestDisplayAmount(message, messages, currentEscrow, { status }) ?? 0;
  if (status === "accepted") {
    const historicalBefore = getReleaseRequestHistoricalBefore(message, amount);
    if (historicalBefore != null) {
      return getEscrowTransition(historicalBefore, amount);
    }
    // Last resort: reconstruct from post-release live escrow + released amount.
    return getEscrowTransition(currentEscrow + amount, amount);
  }
  if (status === "rejected") {
    const historicalBefore = getReleaseRequestHistoricalBefore(message, amount);
    const before = historicalBefore ?? currentEscrow;
    return { before, after: before };
  }
  return getEscrowTransition(currentEscrow, amount);
}

/** Pending sub-milestone batches sort before companion milestone remainder requests. */
export function compareFundReleaseRequestsForDisplay(
  a: { status: string; message: Message; lastActionAt: string },
  b: { status: string; message: Message; lastActionAt: string }
): number {
  if (a.status === "pending" && b.status !== "pending") return -1;
  if (a.status !== "pending" && b.status === "pending") return 1;

  const rank = (item: { status: string; message: Message }) => {
    if (item.status !== "pending") return 10;
    if (item.message.request?.metadata?.isSubmilestone) return 0;
    if (isPartialMilestoneRemainderRelease(item.message)) return 1;
    return 2;
  };

  const rankDiff = rank(a) - rank(b);
  if (rankDiff !== 0) {
    return rankDiff;
  }

  return new Date(b.lastActionAt).getTime() - new Date(a.lastActionAt).getTime();
}

export function getEscrowTransition(beforeEscrow: number | null | undefined, amount: number | null | undefined) {
  const before = Number(beforeEscrow || 0);
  const releaseAmount = Number(amount || 0);
  return {
    before,
    after: Math.max(0, before - releaseAmount),
  };
}

export function isActionRequiredRequest(message: Message): boolean {
  return (
    message.senderRole === SenderRole.PROJECT_MANAGER &&
    !!message.requestId &&
    !message.responseId &&
    message.request?.requestType !== RequestType.DISPUTE
  );
}

export function countActionRequiredRequests(messages?: Message[] | null): number {
  return (messages || []).filter(isActionRequiredRequest).length;
}

export function shouldHideRequestFromChat(message: Message): boolean {
  const requestType = message.request?.requestType ?? message.response?.messageRequest?.requestType;

  if (message.senderRole === SenderRole.SYSTEM) {
    return !message.metadata?.chatEventKind && requestType !== RequestType.DISPUTE;
  }

  if (!requestType) {
    return false;
  }

  return [RequestType.MILESTONE_COMPLETED, RequestType.DRAWDOWN, RequestType.DELAY, RequestType.DISPUTE].includes(requestType);
}

export function isApprovedRequest(message: Message): boolean {
  return message.response?.status === ResponseStatus.ACCEPTED;
}

export function getChatEventDisplayTitle(message: Message): string {
  switch (message.metadata?.chatEventKind) {
    case ChatEventKind.MILESTONE_COMPLETE:
      return "Milestone complete";
    case ChatEventKind.DRAWDOWN_RELEASED:
      return "Drawdown released";
    case ChatEventKind.SUB_MILESTONE_RELEASE: {
      if (message.metadata?.milestoneCompletedWithSubs) {
        return "Milestone and sub-milestones released";
      }
      const subCount = message.metadata?.subMilestones?.length ?? 0;
      return subCount > 1 ? "Sub-milestones released" : "Sub-milestone released";
    }
    case ChatEventKind.MONEY_RELEASE:
      return "Funds released";
    case ChatEventKind.PROJECT_ACCEPTED:
      return "Project accepted";
    default:
      return "Update";
  }
}
